// © Microsoft Corporation. All rights reserved.

import { Provider, connect } from "react-redux";
import { Store, StoreEnhancer, compose, createStore } from "redux";
import { getPageTimings, logger, perfMarker } from "@msnews/diagnostics";

import { DynamicReducer } from "../reducers/DynamicReducer";
import { IDataConnector } from "../connectors/IDataConnector";
import { JSSManager } from "@microsoft/fast-jss-manager-react";
import React from "react";
import ReactDom from "react-dom";
import { applyMiddleware } from "redux-subspace";
import { canUseDOM } from "@msnews/isomorphic-util";
import { create } from "jss";
import { createGenerateClassName } from "./PageHelper";
import { getStateFromRenderCache } from "@msnews/render-cache";
import jssGlobal from "jss-global";
import jssNested from "jss-nested";

declare global {
    interface Window {

        /**
         * Enable redux devtools extension debugging
         */
        __REDUX_DEVTOOLS_EXTENSION_COMPOSE__?: any;

        /**
         * Boolean flag to indicate if render cache hydration is happening
         */
        needsHydrate?: boolean;

        /**
         * Boolean flag to indicate if state is stored in render cache along with DOM
         */
        hasPreHydrateState?: boolean;
    }
}

/**
 * Page Base.
 * Each page or hybrid page/experience must have an instance of the PageBase, which contains the  the store, and dynamic reducer
 * for the experience/app.
 * PageBase is setup as a singleton, so the proper usage in consumers is:
 * var pageBase = PageBase.getInstance();
 * @export
 * @class
 */
class PageBase {
    /**
     * The singleton instance of this class
     * @type: {PageBase}
     */
    private static _instance: PageBase;

    /**
     * The Dynamic reducer for this app/experience
     * @type: {DynamicReducer}
     */
    private _rootReducer: DynamicReducer;

    /**
     * The Redux store containing the top level application state. All component and data connectors will manage namespaced subspaces within
     * this store
     * @type {Store}
     */
    private _store: Store<any>;

    /**
     * Constructor for PageBase class
     * @constructor
     * @param {any} initialState Optional initial redux state provided as part of SSR response during hydration
     */
    private constructor(initialState?: any) {
        /**
         * In debug build, add the option to turn on redux-devtools-extension debugging.
         * More info here https://github.com/zalmoxisus/redux-devtools-extension
         */
        let enhancer: StoreEnhancer<any>;
        const middlewares: any = [];
        if (canUseDOM() && ((typeof logger !== "undefined") && logger ? logger.isDebug() : false)) {
            const composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ && window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__({ trace: true, traceLimit: 25 }) || compose;
            enhancer = composeEnhancers(applyMiddleware(...middlewares)
            );
        } else {
            enhancer = applyMiddleware(...middlewares);
        }

        // Create the root reducer
        this._rootReducer = new DynamicReducer();

        // Check to see if this app instance was bootstrapped with a render-cache
        initialState = initialState || (canUseDOM() && window.needsHydrate && getStateFromRenderCache()) || {};
        if (canUseDOM()) {
            window.hasPreHydrateState = !!(initialState && Object.keys(initialState).length !== 0);
        }

        // create the store with a basic root reducer
        this._store = createStore(this.rootReducer.reduce, initialState, enhancer);

    }

    /**
     * Reset PageBase. Useful when handling multiple requests when server side rendering.
     */
    public static resetPageBaseInstance(initialState?: any): void {
        this._instance = new PageBase(initialState);
    }

    /**
     * Gets the Redux store for this app/experience
     * @method
     * @returns {Store<any>} The overall state for the App, which contains sub-states for each connector
     */
    public get store(): Store<any> {
        return this._store;
    }

    /**
     * Gets the root reducer for this app/experience
     * @returns {DynamicReducer} The reducer for the app/experience
     */
    public get rootReducer(): DynamicReducer {
        return this._rootReducer;
    }

    /**
     * Gets the singleton instance of this class for the app/experience
     * @returns {PageBase} The instance of PageBase for the page/hybrid experience
     */
    public static getInstance(initialState?: any): PageBase {
        return this._instance || (this._instance = new this(initialState));
    }

    /**
     * Starts the application by calling the onApplicationStart method for each connector that has been added to the app.
     * @function
     * @method
     */
    public start(): void {
        // Iterate over all of the connector's and call their OnApplicationStart method
        const allConnectors: IDataConnector<any, any>[] = this.rootReducer.connectors();
        allConnectors.forEach(connector => connector.onApplicationStart());
    }

    /**
     * Creates and renders a root element, that is connected to the store.
     * The root element will created with the passed in connected element as a child.
     * @function
     * @param {React.ReactElement<any>} connectedTopLevelElement The top level rendering element for the page/application.
     * @param {HTMLElement} hostElement The host element (usually a <div>) to contain the msnews-experiences-redux application
     * @param {Function} callback An optional callback to be fired after hydration is complete, used when hydrating server side rendered content.
     * @param {boolean} bypassJSS To bypass JSSManager instance update set this to false
     * @param {Boolean} isSSR An optional flag to signal page is server side rendered and hydration is needed instead of render.
     * @method
     */
    public renderRootElement(
        connectedTopLevelElement: React.ReactElement<any>,
        hostElement: HTMLElement,
        callback?: Function,
        bypassJSS?: Boolean,
        isSSR?: Boolean): void {
        // Update the base JSS manager instance
        if (bypassJSS !== true) {
            this.updateJssManagerInstance();
        }

        // Create a ReactElement, that contains the Redux provider with the store, passing the incoming top level element in as a child, so that
        // the pageElement wraps the top level element with the redux store
        const rootElement = React.createElement(Provider, { store: this._store }, connectedTopLevelElement);

        // Capture Render cache state in page timings
        const pageTimings = getPageTimings();

        if (isSSR) {
            pageTimings["isRenderCachePresent"] = 0;
            perfMarker.startMark("SSR.Hydrate");
            ReactDom.hydrate(rootElement, hostElement, callback);
            perfMarker.endMark("SSR.Hydrate");
            window.needsHydrate = false;
        } else if (window.needsHydrate) {
            ReactDom.hydrate(rootElement, hostElement);
            pageTimings["isRenderCachePresent"] = 1;
            window.needsHydrate = false;
        } else {
            perfMarker.startMark("CSR.Render");
            pageTimings["isRenderCachePresent"] = 0;
            ReactDom.render(rootElement, hostElement);
            perfMarker.endMark("CSR.Render");
        }
    }

    /**
     * Updates the base JSSManager instance with the correct default plugins
     */
    public updateJssManagerInstance(): void {
        const jssInstance: ReturnType<create> = create({
            plugins: [jssNested(), jssGlobal()]
        });

        JSSManager.createGenerateClassName = createGenerateClassName;
        JSSManager.jss = jssInstance;
    }
}

export default PageBase;